/**
 * Where the year actually went.
 *
 * `periods` already derives the day-by-day answer — Berlin is never stored,
 * it is what is left when no stay covers a day — so this only has to count.
 * Doug asked for total days in Berlin, the percentage, total days at the
 * second home (Ireland, for him), total days travel and their percentages.
 *
 * Two decisions worth stating, because both change the numbers:
 *
 * **A year is counted whole, past included.** "40% in Berlin" over the
 * remaining three months is not a fact about the year, and the Year screen's
 * list defaults to hiding the past — which is right for a list of things to
 * do and wrong for a total.
 *
 * **A visit is not a place.** `periods` already treats the second home and Travel as
 * the only stays that move you, with `visit` overlapping whatever you are
 * already doing; Rachel visiting Berlin is still a day in Berlin. Counting
 * visits here would double the year.
 *
 * No imports, so `npm test` can load it.
 */

export interface YearStats {
  year: number
  /** Days counted, which is 365 or 366 — a whole year, not a remainder. */
  days: number
  berlin: number
  /** Days at the profile's second home; 0 for a person with none. */
  home2: number
  travel: number
  /** Whole percentages that sum to 100, largest remainder first.
   *
   *  Rounding each share independently gives 46 + 31 + 24 = 101, and three
   *  numbers under a heading that do not add up read as a bug in the app
   *  rather than as rounding. */
  pct: { berlin: number; home2: number; travel: number }
}

type Counted = { place: string; start: string; end: string }

const at = (iso: string) => Date.parse(`${iso}T12:00:00Z`)
const dayCount = (a: string, b: string) => Math.round((at(b) - at(a)) / 86400000) + 1

export function yearStats(periods: Counted[], year: number): YearStats {
  const from = `${year}-01-01`
  const to = `${year}-12-31`
  const days = dayCount(from, to)

  // Keyed by `Period.place`: `'Home'` since C2.1, which was `'Berlin'` (trap T1).
  const tally: Record<string, number> = { Home: 0, Home2: 0, Travel: 0 };
  for (const p of periods) {
    // Clamped, because a stay can start in December and end in January and
    // both years have a claim on it — each takes the part that is its own.
    const start = p.start > from ? p.start : from
    const end = p.end < to ? p.end : to
    if (start > end) continue
    if (tally[p.place] === undefined) continue
    tally[p.place] += dayCount(start, end)
  }

  return {
    year,
    days,
    berlin: tally.Home,
    home2: tally.Home2,
    travel: tally.Travel,
    pct: shares(tally.Home, tally.Home2, tally.Travel, days),
  }
}

/** Whole percentages that sum to 100, by largest remainder. */
function shares(berlin: number, home2: number, travel: number, total: number) {
  if (!total) return { berlin: 0, home2: 0, travel: 0 }
  const raw = [
    { k: 'berlin' as const, v: (berlin / total) * 100 },
    { k: 'home2' as const, v: (home2 / total) * 100 },
    { k: 'travel' as const, v: (travel / total) * 100 },
  ]
  const out = { berlin: 0, home2: 0, travel: 0 }
  for (const r of raw) out[r.k] = Math.floor(r.v)
  let left = Math.round(raw.reduce((n, r) => n + r.v, 0)) - (out.berlin + out.home2 + out.travel)
  for (const r of [...raw].sort((a, b) => (b.v % 1) - (a.v % 1))) {
    if (left <= 0) break
    out[r.k]++
    left--
  }
  return out
}
